import { useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useStream } from "@/contexts/StreamContext";
import { usePersona } from "@/contexts/PersonaContext";
import { useNavigation } from "@/contexts/NavigationContext";
import { useBooking } from "@/contexts/BookingContext";

/**
 * Custom hook to manage browser history for SPA navigation.
 * Handles back button navigation between views and prevents
 * exiting the app when on root screens.
 */
export function useRouterHistory() {
  const { authState } = useAuth();
  const { stream, setStream } = useStream();
  const { persona, setPersona } = usePersona();
  const { nav, navDispatch } = useNavigation();
  const { bookingDispatch } = useBooking();

  const isPopping = useRef(false);

  // Compute effective screen key for history tracking
  const effectiveScreen = useMemo(() => {
    if (authState === "loggedOut") return "login";
    if (authState === "loading") return "loading";
    if (!stream) return "stream-select";
    if (!persona) return "persona-select";
    return nav.view;
  }, [authState, stream, persona, nav.view]);

  // Seed the very first history entry so back never exits the app
  const didSeed = useRef(false);
  useEffect(() => {
    if (!didSeed.current) {
      window.history.replaceState({ screen: effectiveScreen, synqus: true }, "", "");
      didSeed.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Push history entry whenever screen changes (but not on popstate)
  const prevScreen = useRef(effectiveScreen);
  useEffect(() => {
    if (effectiveScreen === prevScreen.current) return;
    if (isPopping.current) {
      isPopping.current = false;
      prevScreen.current = effectiveScreen;
      return;
    }
    window.history.pushState({ screen: effectiveScreen, synqus: true }, "", "");
    prevScreen.current = effectiveScreen;
  }, [effectiveScreen]);

  // Keep a ref so popstate always reads the latest value
  const screenRef = useRef(effectiveScreen);
  useEffect(() => {
    screenRef.current = effectiveScreen;
  }, [effectiveScreen]);

  // Handle browser back button
  useEffect(() => {
    const handlePop = (_e: PopStateEvent) => {
      const screen = screenRef.current;

      // Root screens — block going further back
      if (screen === "stream-select" || screen === "login" || screen === "loading") {
        window.history.pushState({ screen, synqus: true }, "", "");
        return;
      }

      isPopping.current = true;

      if (screen === "persona-select") {
        setStream(null);
      } else if (screen === "landing") {
        setPersona(null);
      } else if (screen === "pick" || screen === "analytics" || screen === "links") {
        navDispatch({ type: "SET_VIEW", view: "landing" });
      } else if (screen === "confirm") {
        navDispatch({ type: "SET_VIEW", view: "pick" });
      } else if (screen === "done") {
        navDispatch({ type: "NAV_RESET" });
        bookingDispatch({ type: "BOOKING_RESET" });
      }
    };

    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [navDispatch, bookingDispatch, setPersona, setStream]);

  return effectiveScreen;
}
